import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";

// useKeyboardOffset — the ONE keyboard-handling driver site-wide.
// Per the 2026-05-23 audit + plan (see ~/.claude/plans/dapper-marinating-flamingo.md
// and ~/.claude/projects/-Users-trevorfallbacher-mise/memory/feedback_keyboard_one_strategy.md).
//
// RESPONSIBILITIES (only these — never extend without re-auditing
// the existing handlers; multiple strategies stacking is what broke
// the app and triggered the audit).
//
//   1. Write `--kb-offset` (and its alias `--kb-h`) on the document
//      root, tracking the current keyboard height. Source:
//        - @capacitor/keyboard events on native iOS
//        - visualViewport API on web
//      Consumers read this via:
//        - CSS var(--kb-offset) — e.g. .kb-lift / .kb-pad / .kb-sticky-input
//        - Layer-1 attribute selector in public/index.html for the
//          inline-styled flex-end bottom-drawer pattern (~18 screens
//          auto-handled without per-component code).
//
//   2. Install ONE document-level focusin listener that scrolls the
//      focused input into view 280ms after focus. Replaces the prior
//      per-input `useScrollIntoViewOnFocus` hook + every per-component
//      scrollIntoView call. One listener, one timer, no races.
//
//   3. (2026-06-12, per Trevor: "fix this once and for all,
//      universally") Auto-pad the focused input's NEAREST SCROLLABLE
//      ANCESTOR by the keyboard height — the KeyboardAvoidingView /
//      adjustResize technique every native app uses. This is the
//      missing half of #2: scrollIntoView can only center a field if
//      the container has room to scroll, and fields near the END of
//      a surface have none until the container's bottom padding
//      grows. Previously each surface had to opt in (.kb-pad) and
//      every new screen forgot. Now it's automatic:
//        - pads only genuinely scrollable elements (overflow-y
//          auto/scroll + actual overflow) — NEVER bare position:fixed
//          hosts. That distinction is exactly what the removed
//          data-kb-host strategy got wrong (it padded full-height
//          drawers and compressed them past the viewport top).
//        - skips inputs inside the layer-1 drawer pattern
//          (position:fixed + flex-end) — those overlays already lift
//          by --kb-offset via the index.html attribute selector;
//          padding inside them would double-compensate.
//        - stores the container's pre-pad inline style and restores
//          it exactly on keyboard hide / focus move / unmount.
//      The .kb-pad class remains for explicit control but new
//      surfaces no longer need it.
//
// REMOVED (intentionally — these were part of the six-strategy mess
// the audit untangled; see feedback memory):
//
//   - `installKeyboardHostListener` — auto-tagged every position:fixed
//     ancestor of any focused input with data-kb-host="true". Together
//     with the global CSS rule it added padding-bottom to whatever it
//     matched, which compressed full-height drawers and pushed focused
//     inputs above the viewport.
//
//   - `useScrollIntoViewOnFocus(ref)` — per-input scroll hook with a
//     280ms timer. Replaced by the single root listener below; callers
//     just import and call nothing — focus events bubble to document
//     and the root handler does the work.
//
// New keyboard-related code should NOT add new handlers here or in
// any other file. If a new pattern is needed, audit + consolidate
// first (per feedback memory).

const SCROLL_DELAY_MS = 280;
const FOCUSABLE_SELECTOR = 'input, textarea, [contenteditable], [contenteditable="true"]';
// Below this the "keyboard" is an accessory bar / nothing — don't pad.
const KB_MIN_PX = 40;

// ── Responsibility #3: universal nearest-scrollable padding ─────────
// One padded container at a time (you can only type in one field).
// `padded` remembers the element and its ORIGINAL inline
// padding-bottom (null = none) plus the computed base at pad time, so
// keyboard-height changes re-derive from the true base instead of
// compounding, and restore puts back exactly what the component set.
let padded = null;

function isScrollable(el) {
  if (!(el instanceof HTMLElement)) return false;
  const oy = getComputedStyle(el).overflowY;
  if (oy !== "auto" && oy !== "scroll") return false;
  return el.scrollHeight > el.clientHeight + 1;
}

// Layer-1 drawer detection — overlays that match the index.html
// attribute selector (position:fixed flex flex-end) already lift by
// --kb-offset; inputs inside them are compensated at the overlay
// level and must NOT also pad their inner scroll area.
function insideLiftedDrawer(start) {
  for (let el = start; el && el !== document.body; el = el.parentElement) {
    const cs = getComputedStyle(el);
    if (cs.position === "fixed" && cs.display === "flex" && cs.alignItems === "flex-end") {
      return true;
    }
  }
  return false;
}

function findScrollContainer(start) {
  for (let el = start?.parentElement; el && el !== document.body; el = el.parentElement) {
    if (isScrollable(el)) return el;
  }
  const root = document.scrollingElement || document.documentElement;
  return root instanceof HTMLElement ? root : null;
}

function restoreScrollPadding() {
  if (!padded) return;
  const { el, prevInline } = padded;
  padded = null;
  if (!el?.isConnected) return;
  if (prevInline == null) el.style.removeProperty("padding-bottom");
  else el.style.paddingBottom = prevInline;
}

// Re-evaluate padding against the CURRENT focus + keyboard height.
// Called from every keyboard event, the web visualViewport updates,
// and focus moves — idempotent, cheap (a short ancestor walk).
function syncScrollPadding(offsetPx) {
  const ae = document.activeElement;
  const focused = ae && ae.matches?.(FOCUSABLE_SELECTOR) ? ae : null;
  if (!focused || offsetPx < KB_MIN_PX) {
    restoreScrollPadding();
    return;
  }
  if (insideLiftedDrawer(focused)) {
    restoreScrollPadding();
    return;
  }
  const container = findScrollContainer(focused);
  if (!container) return;
  if (padded && padded.el !== container) restoreScrollPadding();
  if (!padded) {
    padded = {
      el: container,
      prevInline: container.style.paddingBottom || null,
      // Computed base BEFORE we touch it — survives height changes.
      base: getComputedStyle(container).paddingBottom || "0px",
    };
  }
  container.style.paddingBottom = `calc(${padded.base} + ${Math.round(offsetPx)}px)`;
}

// One root focusin listener — scrolls the focused input into view
// once, with a 280ms delay so the OS-level keyboard animation has
// started and visualViewport has reported the new size. Idempotent
// across mounts of useKeyboardOffset (we install per-hook-call but
// remove on cleanup, and App.jsx mounts the hook once globally).
function installRootScrollIntoView() {
  let scrollTimer = null;

  const onFocusIn = (e) => {
    const t = e.target;
    if (!t || !t.matches || !t.matches(FOCUSABLE_SELECTOR)) return;
    // Padding first, scroll second: when focus MOVES between fields
    // with the keyboard already up, the new field's container needs
    // its room before the centering scroll fires. Reads the live
    // offset var (0 when the keyboard isn't up yet — the keyboard
    // event path re-syncs the moment a height is known).
    const cur = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue("--kb-offset"),
    ) || 0;
    syncScrollPadding(cur);
    if (scrollTimer) clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => {
      try {
        // block: "center" puts the input mid-viewport when possible
        // — leaves room for typing AND for the keyboard above the
        // input. behavior: "smooth" matches the keyboard animation.
        t.scrollIntoView({ block: "center", behavior: "smooth" });
      } catch {
        try { t.scrollIntoView(); } catch { /* ignore */ }
      }
    }, SCROLL_DELAY_MS);
  };

  document.addEventListener("focusin", onFocusIn, true);
  return () => {
    document.removeEventListener("focusin", onFocusIn, true);
    if (scrollTimer) clearTimeout(scrollTimer);
  };
}

export function useKeyboardOffset() {
  useEffect(() => {
    const root = document.documentElement;
    const isIos =
      Capacitor.isNativePlatform?.() && Capacitor.getPlatform?.() === "ios";

    const setOffset = (px) => {
      const value = `${px}px`;
      root.style.setProperty("--kb-offset", value);
      // Alias for legacy consumers (ScanPipeline.jsx reads --kb-h).
      // One source of truth: both vars hold the same number.
      root.style.setProperty("--kb-h", value);
      root.style.setProperty("--kb-visible", px > 40 ? "1" : "0");
      // Universal scroll-room: pad the focused field's nearest
      // scrollable ancestor by the keyboard height (responsibility
      // #3). Runs on every height change — show, hide, and the
      // accessory-bar resizes iOS fires mid-session.
      syncScrollPadding(px);
    };

    const detachRootScroll = installRootScrollIntoView();

    if (isIos) {
      let showHandle, hideHandle, willShowHandle, willHideHandle;
      (async () => {
        try {
          willShowHandle = await Keyboard.addListener("keyboardWillShow", (info) => {
            setOffset(info?.keyboardHeight || 0);
          });
          showHandle = await Keyboard.addListener("keyboardDidShow", (info) => {
            setOffset(info?.keyboardHeight || 0);
          });
          willHideHandle = await Keyboard.addListener("keyboardWillHide", () => {
            setOffset(0);
          });
          hideHandle = await Keyboard.addListener("keyboardDidHide", () => {
            setOffset(0);
          });
        } catch (e) {
          console.warn("[keyboard-offset] capacitor listener failed:", e?.message || e);
        }
      })();
      return () => {
        try { willShowHandle?.remove?.(); } catch {}
        try { showHandle?.remove?.(); } catch {}
        try { willHideHandle?.remove?.(); } catch {}
        try { hideHandle?.remove?.(); } catch {}
        root.style.removeProperty("--kb-offset");
        root.style.removeProperty("--kb-h");
        root.style.removeProperty("--kb-visible");
        restoreScrollPadding();
        detachRootScroll?.();
      };
    }

    // Web / non-iOS fallback — visualViewport reports the visible
    // inner size when the keyboard opens. The difference vs.
    // window.innerHeight is the keyboard height.
    const vv = window.visualViewport;
    if (!vv) {
      return () => { detachRootScroll?.(); };
    }
    const update = () => {
      const offset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setOffset(offset);
    };
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    update();
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      root.style.removeProperty("--kb-offset");
      root.style.removeProperty("--kb-h");
      root.style.removeProperty("--kb-visible");
      restoreScrollPadding();
      detachRootScroll?.();
    };
  }, []);
}
